import { computed, onUnmounted, ref, Ref, shallowReadonly, watch } from 'vue';
import api from '@/api';
import { PageName } from '@/api/tracking';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import {
  AdpDocument,
  FeedAutoGenerationReason,
  FeedSummaryGenerationState,
  FeedSummaryResponse,
  ThemeSummaryRequest,
} from '@/types';
import { syncAdpDocumentActions } from '@/utils/adpDocument';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';
import { useFeedSummariesTheme } from '@/utils/swr';
import { useEmitter } from '@/utils/vue';

const POLLING_INTERVAL_MS = 2000;

export const useThemeSummary = (
  params: ThemeSummaryRequest,
  articleCount: Ref<number>,
  pageName: PageName,
) => {
  const layoutVariant = useLayoutVariant();
  // ThemeFeedの場合はarticle exclusionおよびsite exclusionの更新があったらtrueにする
  const invalidationConditionUpdated = ref(false);

  const invalidate = () => {
    invalidationConditionUpdated.value = true;
  };
  const emitter = useEmitter();
  emitter.on('article-removed', invalidate);
  emitter.on('article-restored', invalidate);
  emitter.on('personal-feed-article-removed', invalidate);
  emitter.on('personal-feed-article-restored', invalidate);
  onUnmounted(() => {
    emitter.off('article-removed', invalidate);
    emitter.off('article-restored', invalidate);
    emitter.off('personal-feed-article-removed', invalidate);
    emitter.off('personal-feed-article-restored', invalidate);
  });

  // パーソナルニュースが遅延フェッチされるので、それに合わせて
  // 記事があるときに遅延してswrvが発動するようにする。
  const enable = computed(() => articleCount.value > 0);
  const { data: summary, mutate } = useFeedSummariesTheme(params, enable);

  const syncSource = (updated: AdpDocument) => {
    if (summary.value?.generation_status !== 'processed') return;
    syncAdpDocumentActions(summary.value.sources, updated);
  };
  emitter.on('article-updated', syncSource);
  emitter.on('article-viewed', syncSource);
  onUnmounted(() => {
    emitter.off('article-updated', syncSource);
    emitter.off('article-viewed', syncSource);
  });

  const isPolling = ref(false);

  const { createSnackbar } = useSnackbar();
  const showError = (isTimeout: boolean) => {
    const message = isTimeout
      ? '要約生成に失敗しました'
      : '現在こちらの要約を生成することができません';
    const description = isTimeout
      ? '申し訳ありませんが、再度お試しください'
      : '申し訳ありませんが、時間を空けて再度お試しください';

    createSnackbar({
      message,
      description,
      type: 'error',
    });
  };

  // 要約生成ボタン(FeedSummaryGenerationButton)の状態
  const { getCurrentPathForTracking } = useCurrentPathForTracking();
  const isStale = computed(() => {
    return (
      summary.value?.generation_status === 'processed' &&
      summary.value.freshness === 'stale'
    );
  });

  const generateButtonState = computed<FeedSummaryGenerationState>(() => {
    if (articleCount.value === 0) return 'no-articles';

    if (summary.value === undefined) return 'disabled';

    if (isPolling.value) return 'processing';

    const processed =
      summary.value?.generation_status === 'processed' &&
      !isStale.value &&
      !invalidationConditionUpdated.value;
    if (processed) return 'no-update';

    if (isStale.value) return 'ready-stale';

    return 'ready';
  });

  // 要約生成ボタン(FeedSummaryGenerationButton)が押されたときの処理
  let timerId: number;
  let unmounted = false; // mutate中にunmountedしたときにポーリングが止まるようにする
  const generateSummary = async (data?: {
    feature?: // featureが存在するときだけ後段でevent_sourceとして送信される
    'keyword_feed' | 'related_feed' | 'research_paper_feed' | 'patent_feed';
    autoGenReason?: FeedAutoGenerationReason;
  }) => {
    const { feature, autoGenReason } = data ?? {};

    if (summary.value === undefined) return;

    // processedではなかったらポーリング開始
    if (
      summary.value.generation_status !== 'processed' ||
      invalidationConditionUpdated.value ||
      isStale.value
    ) {
      invalidationConditionUpdated.value = false;
      isPolling.value = true;

      // processingでなければ生成リクエストを送信
      if (summary.value.generation_status !== 'processing') {
        await api.requestThemeSummaryGeneration(
          params,
          {
            pageName: pageName,
            url: getCurrentPathForTracking(),
            feature: feature,
            layoutVariant,
          },
          autoGenReason,
        );
      }

      async function poll() {
        await mutate();

        // まだprocessingならpolling続行
        const status = summary.value?.generation_status;
        if (status === 'processing' && !unmounted) {
          timerId = window.setTimeout(poll, POLLING_INTERVAL_MS);
        } else {
          isPolling.value = false;
          if (status === 'error') {
            const isTimeout = summary.value?.error_message === 'timeout';
            showError(isTimeout);
          }
        }
      }
      await poll();
    }
  };
  onUnmounted(() => {
    window.clearTimeout(timerId);
    unmounted = true;
  });

  // 要約(FeedSummary)の表示条件
  const shouldShowSummary = (
    s: FeedSummaryResponse | undefined,
  ): s is FeedSummaryResponse => {
    return (
      s !== undefined &&
      (isPolling.value || s.generation_status === 'processed')
    );
  };

  watch(
    () => summary.value?.generation_status,
    status => {
      // generation_statusがprocessingなのに、isPollingがfalseのときはポーリングを開始する。
      // ポーリング中に画面を切り替えて戻ってきたときにこの状態になる。
      if (
        status === 'processing' &&
        !isPolling.value &&
        !invalidationConditionUpdated.value
      ) {
        generateSummary();
      }
    },
    { immediate: true },
  );

  return {
    summary: shallowReadonly(summary),
    generateButtonState,
    generateSummary,
    shouldShowSummary,
    mutate,
  };
};
